<template>
  <div class="fp-step">
    <div class="fp-step-header">
      <button type="button" class="fp-back-btn" @click="$emit('go-back')" t_label="btn_emp2_Back_ForgotPasswordVerifyCode">
        <img 
          src="https://cdn.adsalecdn.com/EMP/2-0/BuyerIdx/All_Categories.svg" 
          alt="back" 
          class="fp-back-icon"
        />
      </button>
      <h1 class="fp-step-title">{{ $t('lbl_emp2_EnterVerificationCode') }}</h1>
    </div>

    <div class="fp-form-group">
      <div class="fp-otp-info">
        <template v-if="loginMethod === 'email'">
          <div class="fp-otp-info-line">
            <span>{{ $t('lbl_emp2_CodeSentToEmail', { emailInput: '' }).replace(': ', '') }}</span>
          </div>
          <div class="fp-otp-info-target">{{ email }}</div>
        </template>
        <template v-else>
          <div class="fp-otp-info-line">
            <span>{{ $t('lbl_emp2_CodeSentToMobile', { countryCode: '', mobileNumber: '' }).replace('. ', '') }}</span>
          </div>
          <div class="fp-otp-info-target">{{ countryCode }} {{ mobileNumber }}</div>
        </template>
      </div>
      <div class="fp-otp-input-group">
        <label class="fp-otp-label">
          <template v-if="loginMethod === 'email' || loginMethod === 'mobile'">
            {{ $t('lbl_emp2_PleaseEnterCodeToContinue') }}:
          </template>
          <template v-else>
            {{ $t('lbl_emp2_PleaseEnterCodeToLogin') }}:
          </template>
        </label>
        <div class="fp-otp-inputs" :class="{ 'fp-otp-error': otpError }">
          <input
            v-for="(digit, index) in otpDigits"
            :key="index"
            :value="digit"
            type="text"
            class="fp-otp-input"
            :class="{ 'fp-otp-input-error': otpError }"
            maxlength="1"
            @input="handleOtpInput(index, $event)"
            @keydown="handleOtpKeydown(index, $event)"
            @paste="handleOtpPaste($event)"
            @focus="$emit('clear-error')"
          />
        </div>
        <div v-if="otpError" class="fp-otp-error-message">
          {{ $t('lbl_emp2_InvalidVerificationCode') }}
        </div>
      </div>
      <div class="fp-otp-help-text">
        <template v-if="loginMethod === 'email'">
          <p v-html="$t('lbl_emp2_IfNotReceivedEmailVerification')"></p>
        </template>
        <template v-else>
          <p v-html="$t('lbl_emp2_IfNotReceivedSMSVerification')"></p>
        </template>
      </div>
      <button
        class="fp-btn-resend"
        :disabled="resendCountdown > 0"
        @click="$emit('resend-code')"
        t_label="btn_emp2_ResendCode_ForgotPasswordVerifyCode"
      >
        {{ resendCountdown > 0 ? $t('lbl_emp2_ResendAfterSec', { countDown: resendCountdown }) : $t('lbl_emp2_ResendCode') }}
      </button>
    </div>

  </div>
</template>

<script>
export default {
  name: 'ForgotPasswordVerifyCode',
  props: {
    otpDigits: {
      type: Array,
      default: () => ['', '', '', '']
    },
    otpError: {
      type: Boolean,
      default: false
    },
    loginMethod: {
      type: String,
      required: true
    },
    email: {
      type: String,
      default: ''
    },
    mobileNumber: {
      type: String,
      default: ''
    },
    countryCode: {
      type: String,
      default: '+86'
    },
    resendCountdown: {
      type: Number,
      default: 0
    },
    loading: {
      type: Boolean,
      default: false
    }
  },
  computed: {
    isOtpComplete() {
      return this.otpDigits.every(digit => digit && digit.length === 1);
    }
  },
  methods: {
    handleOtpInput(index, event) {
      const value = event.target.value.replace(/[^0-9]/g, '').replace(/\s/g, '').slice(0, 1);
      const newDigits = [...this.otpDigits];
      newDigits[index] = value;
      this.$emit('update:otpDigits', newDigits);
      
      // 自动聚焦到下一个输入框
      if (value && index < this.otpDigits.length - 1) {
        this.$nextTick(() => {
          const nextInput = event.target.parentElement.children[index + 1];
          if (nextInput) {
            nextInput.focus();
          }
        });
      }
      
      // 如果4位都填满了，自动验证并跳转
      this.$nextTick(() => {
        if (newDigits.every(digit => digit && digit.length > 0) && newDigits.length === 4) {
          this.$emit('continue');
        }
      });
    },
    handleOtpKeydown(index, event) {
      // 处理退格键
      if (event.key === 'Backspace' && !this.otpDigits[index] && index > 0) {
        const prevInput = event.target.parentElement.children[index - 1];
        if (prevInput) {
          prevInput.focus();
        }
      }
    },
    handleOtpPaste(event) {
      event.preventDefault();
      const pastedData = event.clipboardData.getData('text').replace(/[^0-9]/g, '').replace(/\s/g, '').slice(0, this.otpDigits.length);
      const newDigits = [...this.otpDigits];
      for (let i = 0; i < pastedData.length && i < newDigits.length; i++) {
        newDigits[i] = pastedData[i];
      }
      this.$emit('update:otpDigits', newDigits);
      
      // 聚焦到最后一个填充的输入框
      const lastFilledIndex = Math.min(pastedData.length - 1, this.otpDigits.length - 1);
      this.$nextTick(() => {
        const nextInput = event.target.parentElement.children[lastFilledIndex + 1];
        if (nextInput) {
          nextInput.focus();
        } else {
          event.target.blur();
        }
        
        // 如果4位都填满了，自动验证并跳转
        if (newDigits.every(digit => digit && digit.length > 0) && newDigits.length === 4) {
          this.$emit('continue');
        }
      });
    }
  }
};
</script>

<style scoped>
.fp-step {
  gap: 24px !important;
  padding: 40px 24px !important;
  margin: -40px -24px !important;
  width: calc(100% + 48px) !important;
  display: flex !important;
  flex-direction: column !important;
  align-items: flex-start !important;
  justify-content: flex-start !important;
  align-self: stretch !important;
}

.fp-step-header {
  display: flex;
  align-items: center;
  gap: 10px;
  width: 100%;
}

.fp-back-btn {
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: transparent;
  border: none;
  cursor: pointer;
  padding: 0;
  flex-shrink: 0;
  position: relative;
}

.fp-back-btn:hover {
  background: rgba(0, 0, 0, 0.05);
  border-radius: 50%;
}

.fp-back-btn .fp-back-icon {
  width: 20px;
  height: 20px;
  transform: scaleX(-1);
}

.fp-step-title {
  color: rgba(0, 0, 0, 0.80);
  font-size: 28px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 36.40px;
  margin: 0;
  word-wrap: break-word;
  flex: 1;
}

@media (max-width: 768px) {
  .fp-step-title {
    font-size: 24px;
    line-height: 31.20px;
  }
}

.fp-form-group {
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  display: flex;
  gap: 12px;
  width: 100%;
}

.fp-otp-info {
  align-self: stretch;
  flex-direction: row;
  flex-wrap: wrap;
  justify-content: flex-start;
  align-items: center;
  gap: 8px;
  display: flex;
}

.fp-otp-info-line {
  color: rgba(0, 0, 0, 0.80);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 18.20px;
  word-wrap: break-word;
  margin: 0;
  padding: 0;
}

.fp-otp-info-target {
  color: rgba(0, 0, 0, 0.80);
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 18.20px;
  word-wrap: break-word;
  margin: 0;
  padding: 0;
}

.fp-otp-input-group {
  align-self: stretch;
  flex-direction: column;
  justify-content: flex-start;
  align-items: flex-start;
  gap: 8px;
  display: flex;
}

.fp-otp-label {
  color: rgba(0, 0, 0, 0.80);
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 15.60px;
  word-wrap: break-word;
  margin: 0;
  padding: 0;
}

.fp-otp-inputs {
  justify-content: flex-start;
  align-items: flex-start;
  gap: 4px;
  display: inline-flex;
}

.fp-otp-inputs.fp-otp-error {
  border-color: #CD1515;
}

.fp-otp-input {
  width: 32px;
  padding-left: 8px;
  padding-right: 8px;
  padding-top: 0;
  padding-bottom: 0;
  border: none;
  border-radius: 4px;
  outline: 1px rgba(0, 0, 0, 0.23) solid;
  outline-offset: -1px;
  background: white;
  text-align: center;
  font-size: 24px;
  font-family: 'Inter', sans-serif;
  font-weight: 600;
  color: rgba(0, 0, 0, 0.80);
  transition: outline-color 0.2s;
  box-sizing: border-box;
  min-height: 48px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.fp-otp-input:focus {
  outline: 1px #7366FF solid;
  outline-offset: -1px;
}

.fp-otp-input.fp-otp-input-error {
  outline-color: #CD1515 !important;
}

.fp-otp-error-message {
  padding-left: 14px;
  padding-right: 14px;
  color: #D32F2F;
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  letter-spacing: 0.40px;
  word-wrap: break-word;
}

.fp-otp-help-text {
  align-self: stretch;
  color: rgba(0, 0, 0, 0.60);
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 15.60px;
  word-wrap: break-word;
  margin: 0;
  padding: 0;
}

.fp-otp-help-text p {
  margin: 0;
  padding: 0;
  color: rgba(0, 0, 0, 0.60);
  font-size: 12px;
  font-family: 'Inter', sans-serif;
  font-weight: 400;
  line-height: 15.60px;
}

.fp-otp-help-text strong {
  font-weight: 700;
  color: rgba(0, 0, 0, 0.60);
  font-size: 12px;
  line-height: 15.60px;
}

.fp-btn-resend {
  align-self: stretch;
  padding: 15px;
  background: #5D49C4; /* 原型启用态背景 */
  border: none;
  border-radius: 999px;
  color: white;
  font-size: 14px;
  font-family: 'Inter', sans-serif;
  font-weight: 700;
  line-height: 14px;
  cursor: pointer;
  text-align: center;
  justify-content: center;
  align-items: center;
  display: inline-flex;
  transition: all 0.2s;
}

.fp-btn-resend:hover:not(:disabled) {
  background: #4a3ba5;
}

.fp-btn-resend:disabled {
  cursor: not-allowed;
  background: #9EA5A8; /* 原型倒计时禁用态背景 */
  color: white; /* 原型文字颜色：白色 */
  opacity: 1;
}

.fp-btn-primary {
  width: 100%;
  padding: 16px 24px !important;
  background: #7366FF !important;
  color: white !important;
  border: none !important;
  border-radius: 999px !important;
  font-size: 14px !important;
  font-family: 'Inter', sans-serif !important;
  font-weight: 600 !important;
  line-height: 14px !important;
  cursor: pointer !important;
  transition: all 0.2s !important;
}

.fp-btn-primary:hover:not(:disabled) {
  background: #5a4dcc !important;
  color: white !important;
  border: none !important;
}

.fp-btn-primary:disabled {
  opacity: 0.6 !important;
  cursor: not-allowed !important;
  background: #B8B3FF !important;
  color: rgba(255, 255, 255, 0.9) !important;
}
</style>

